/**
 * PontosContagemTab - Tab showing counting locations per hospital for client
 */
import React, { useMemo, useState } from 'react';
import { MapPin, Plus, Loader2, Search } from 'lucide-react';
import type { ClienteHospital } from '../../types/clienteHospital';

interface PontosContagemTabProps {
  hospitais: ClienteHospital[];
  loading: boolean;
  onOpenVincularLocais: (hospitalId: number, hospitalNome: string) => void;
}

export const PontosContagemTab: React.FC<PontosContagemTabProps> = ({
  hospitais,
  loading,
  onOpenVincularLocais,
}) => {
  // #10: busca genérica — nome do hospital, código, cidade, estado, CNPJ.
  const [busca, setBusca] = useState('');
  const hospitaisFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return hospitais;
    const termoDigitos = termo.replace(/\D/g, '');
    return hospitais.filter((h) => {
      const campos = [
        h.hospital_nome, h.hospital_codigo, h.codigo,
        h.hospital_cidade, h.hospital_estado, h.hospital_cnpj,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      const matchTexto = campos.some((c) => c.includes(termo));
      const matchCnpj = termoDigitos.length > 0 &&
        String(h.hospital_cnpj || '').replace(/\D/g, '').includes(termoDigitos);
      return matchTexto || matchCnpj;
    });
  }, [hospitais, busca]);

  if (loading) {
    return (
      <div className="py-12 flex flex-col items-center justify-center text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
        Carregando hospitais...
      </div>
    );
  }

  if (hospitais.length === 0) {
    return (
      <div className="py-12 text-center text-gray-500">
        <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Nenhum hospital vinculado a este cliente.</p>
        <p className="text-sm mt-1">Vincule hospitais na aba "Hospitais Atendidos" primeiro.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Configure quais locais de contagem de cada hospital se aplicam a este cliente.
        </h3>
        {/* #10: busca genérica (nome do hospital, código, cidade, estado, CNPJ) */}
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por hospital, código, cidade, estado ou CNPJ..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Codigo
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Hospital
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Cidade
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                CNPJ
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Locais Totais
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Locais Cliente
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Acoes
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {hospitaisFiltrados.map((h) => {
              // N232: a contagem vem na própria lista. Pedir hospital a hospital
              // gerava centenas de requisições (e 404 em rajada ao trocar de
              // cliente), e o CrowdSec do homolog baniu o escritório por isso.
              const locaisCliente = h.locais_cliente_count ?? 0;
              return (
                <tr key={h.hospital_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                    {h.hospital_codigo || h.codigo || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                    {h.hospital_nome}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {h.hospital_cidade || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {h.hospital_estado || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {h.hospital_cnpj || '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                      {h.locais_count ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      locaisCliente > 0
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                    }`}>
                      {locaisCliente}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => onOpenVincularLocais(h.hospital_id, h.hospital_nome)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                      title="Vincular locais de contagem"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Vincular Locais
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
